import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { toast, Toaster } from "sonner";
import { MonthYearPicker } from "../components/Filters";
import { MONTHS, formatRupiah } from "../lib/format";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Download, FileText, Printer, Loader2, BarChart3 } from "lucide-react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const YEARS = [2025, 2026, 2027, 2028];
const shortMonth = (m) => MONTHS[m - 1].slice(0, 3);

function downloadCSV(filename, rows) {
  const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

async function exportPDF(node, filename) {
  const canvas = await html2canvas(node, { scale: 2, useCORS: true, backgroundColor: "#ffffff", logging: false });
  const pdf = new jsPDF("p", "mm", "a4");
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const imgW = pageW - 16;
  const imgH = (canvas.height * imgW) / canvas.width;
  let heightLeft = imgH;
  let position = 8;
  pdf.addImage(canvas.toDataURL("image/png"), "PNG", 8, position, imgW, imgH);
  heightLeft -= (pageH - 16);
  while (heightLeft > 0) {
    position = heightLeft - imgH + 8;
    pdf.addPage();
    pdf.addImage(canvas.toDataURL("image/png"), "PNG", 8, position, imgW, imgH);
    heightLeft -= (pageH - 16);
  }
  pdf.save(filename);
}

export default function Reports() {
  const [year, setYear] = useState(2026);
  const [month, setMonth] = useState(8);
  const [yYear, setYYear] = useState(2026);
  const [monthly, setMonthly] = useState(null);
  const [yearly, setYearly] = useState(null);
  const [busy, setBusy] = useState(false);
  const monthlyRef = useRef(null);
  const yearlyRef = useRef(null);

  const loadMonthly = () => api.get(`/reports/monthly?year=${year}&month=${month}`).then((r) => setMonthly(r.data)).catch(() => toast.error("Gagal memuat laporan bulanan"));
  const loadYearly = () => api.get(`/reports/yearly?year=${yYear}`).then((r) => setYearly(r.data)).catch(() => toast.error("Gagal memuat laporan tahunan"));
  useEffect(() => { loadMonthly(); }, [year, month]);
  useEffect(() => { loadYearly(); }, [yYear]);

  const withBusy = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error("Gagal export"); } finally { setBusy(false); } };

  const csvMonthly = () => {
    if (!monthly) return;
    const rows = [["Laporan Bulanan", `${MONTHS[month - 1]} ${year}`], [], ["Nama Guru/Staf", "Total Sesi", "Total Teaching Fee", "Status"]];
    monthly.fee_rows.forEach((r) => rows.push([r.teacher_name, r.total_sessions, r.total, r.payment_status === "paid" ? "Sudah Dibayar" : "Belum Dibayar"]));
    const f = monthly.finance;
    rows.push([], ["Total Gaji", f.total_gaji], ["Tabungan THR", f.thr], ["Maintenance/DPO", f.maintenance_dpo], ["Pengeluaran Lain", f.expenses_other], ["Total Pengeluaran", f.total_pengeluaran], ["Total Penerimaan", f.total_penerimaan], ["Saldo Bersih", f.saldo_bersih]);
    downloadCSV(`Laporan-Bulanan-${MONTHS[month - 1]}-${year}.csv`, rows);
  };

  const csvYearly = () => {
    if (!yearly) return;
    const rows = [["Laporan Tahunan", yYear], [], ["Nama Guru/Staf", "Total Sesi", "Total Teaching Fee"]];
    yearly.per_teacher.forEach((t) => rows.push([t.teacher_name, t.total_sessions, t.total_fee]));
    rows.push([], ["Sekolah", "Total Sesi", "Total Penerimaan"]);
    yearly.per_school.forEach((s) => rows.push([s.school_name, s.total_sessions, s.total_penerimaan]));
    rows.push([], ["Bulan", "Penerimaan", "Pengeluaran", "Saldo"]);
    yearly.monthly.forEach((m) => rows.push([MONTHS[m.month - 1], m.penerimaan, m.pengeluaran, m.saldo]));
    rows.push([], ["Total Penerimaan", yearly.total_penerimaan], ["Total Pengeluaran", yearly.total_pengeluaran], ["Total THR", yearly.total_thr], ["Total Saldo Bersih", yearly.total_saldo]);
    downloadCSV(`Laporan-Tahunan-${yYear}.csv`, rows);
  };

  const statusBadge = (s) => <Badge className={`rounded-full ${s === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>{s === "paid" ? "Sudah Dibayar" : "Belum Dibayar"}</Badge>;
  const saldoColor = (v) => (v > 0 ? "text-emerald-600" : v < 0 ? "text-rose-600" : "text-gtsorange");

  return (
    <div className="space-y-5" data-testid="reports-page">
      <Toaster position="top-center" richColors />
      <h2 className="font-nunito font-extrabold text-2xl text-gtsnavy">Laporan</h2>

      <Tabs defaultValue="monthly">
        <TabsList className="rounded-full">
          <TabsTrigger value="monthly" className="rounded-full" data-testid="tab-monthly">Bulanan</TabsTrigger>
          <TabsTrigger value="yearly" className="rounded-full" data-testid="tab-yearly">Tahunan</TabsTrigger>
        </TabsList>

        {/* MONTHLY */}
        <TabsContent value="monthly" className="space-y-4">
          <div className="flex flex-wrap gap-2 items-center no-print">
            <MonthYearPicker year={year} month={month} setYear={setYear} setMonth={setMonth} />
            <div className="flex-1" />
            <Button size="sm" variant="outline" onClick={() => withBusy(csvMonthly)} className="rounded-full gap-1" data-testid="monthly-csv"><Download className="w-4 h-4" /> CSV</Button>
            <Button size="sm" variant="outline" onClick={() => withBusy(() => exportPDF(monthlyRef.current, `Laporan-Bulanan-${MONTHS[month - 1]}-${year}.pdf`))} disabled={busy} className="rounded-full gap-1" data-testid="monthly-pdf">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} PDF</Button>
            <Button size="sm" variant="outline" onClick={() => window.print()} className="rounded-full gap-1" data-testid="monthly-print"><Printer className="w-4 h-4" /> Print</Button>
          </div>

          <div ref={monthlyRef} className="space-y-4 bg-gtscream p-1">
            <Card className="rounded-2xl border-none shadow-[0_8px_30px_rgb(16,42,86,0.06)] bg-white overflow-hidden">
              <div className="px-5 py-3 bg-gtsnavy text-white font-nunito font-bold">Rekap Teaching Fee & Sesi · {MONTHS[month - 1]} {year}</div>
              <div className="overflow-x-auto gts-scroll">
                <table className="w-full text-sm">
                  <thead><tr className="text-muted-foreground text-xs border-b">
                    <th className="text-left px-4 py-2">Nama Guru/Staf</th><th className="text-right px-4 py-2">Total Sesi</th><th className="text-right px-4 py-2">Teaching Fee</th><th className="text-left px-4 py-2">Status</th>
                  </tr></thead>
                  <tbody>
                    {monthly?.fee_rows.map((r) => (
                      <tr key={r.teacher_id} className="border-b last:border-0" data-testid={`monthly-fee-${r.teacher_id}`}>
                        <td className="px-4 py-2.5 font-medium text-gtsnavy">{r.teacher_name}</td>
                        <td className="px-4 py-2.5 text-right">{r.total_sessions}</td>
                        <td className="px-4 py-2.5 text-right font-semibold">{formatRupiah(r.total)}</td>
                        <td className="px-4 py-2.5">{statusBadge(r.payment_status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>

            {monthly && (
              <Card className="rounded-2xl border-none shadow-[0_8px_30px_rgb(16,42,86,0.06)] bg-white p-5">
                <h3 className="font-nunito font-bold text-gtsnavy mb-3">Ringkasan Keuangan</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 text-sm">
                  <div className="flex justify-between py-1.5 border-b"><span>Total Gaji 6 Karyawan</span><span>{formatRupiah(monthly.finance.total_gaji)}</span></div>
                  <div className="flex justify-between py-1.5 border-b"><span>Tabungan THR ({monthly.finance.thr_percentage}%)</span><span>{formatRupiah(monthly.finance.thr)}</span></div>
                  <div className="flex justify-between py-1.5 border-b"><span>Maintenance & DPO</span><span>{formatRupiah(monthly.finance.maintenance_dpo)}</span></div>
                  <div className="flex justify-between py-1.5 border-b"><span>Pengeluaran Lain-lain</span><span>{formatRupiah(monthly.finance.expenses_other)}</span></div>
                  <div className="flex justify-between py-1.5 border-b"><span>Total Penerimaan</span><span>{formatRupiah(monthly.finance.total_penerimaan)}</span></div>
                  <div className="flex justify-between py-1.5 border-b"><span>Total Pengeluaran</span><span>{formatRupiah(monthly.finance.total_pengeluaran)}</span></div>
                  <div className="flex justify-between py-2 font-nunito font-extrabold text-gtsnavy sm:col-span-2 mt-1"><span>Saldo Bersih GTS</span><span className={saldoColor(monthly.finance.saldo_bersih)} data-testid="monthly-saldo">{formatRupiah(monthly.finance.saldo_bersih)}</span></div>
                </div>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* YEARLY */}
        <TabsContent value="yearly" className="space-y-4">
          <div className="flex flex-wrap gap-2 items-center no-print">
            <Select value={String(yYear)} onValueChange={(v) => setYYear(Number(v))}>
              <SelectTrigger className="w-[120px] rounded-xl bg-white" data-testid="yearly-year"><SelectValue /></SelectTrigger>
              <SelectContent>{YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
            <div className="flex-1" />
            <Button size="sm" variant="outline" onClick={() => withBusy(csvYearly)} className="rounded-full gap-1" data-testid="yearly-csv"><Download className="w-4 h-4" /> CSV</Button>
            <Button size="sm" variant="outline" onClick={() => withBusy(() => exportPDF(yearlyRef.current, `Laporan-Tahunan-${yYear}.pdf`))} disabled={busy} className="rounded-full gap-1" data-testid="yearly-pdf">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} PDF</Button>
            <Button size="sm" variant="outline" onClick={() => window.print()} className="rounded-full gap-1" data-testid="yearly-print"><Printer className="w-4 h-4" /> Print</Button>
          </div>

          <div ref={yearlyRef} className="space-y-4 bg-gtscream p-1">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card className="rounded-2xl border-none shadow-[0_8px_30px_rgb(16,42,86,0.06)] bg-white overflow-hidden">
                <div className="px-5 py-3 bg-gtsnavy text-white font-nunito font-bold">Total Sesi & Fee per Guru · {yYear}</div>
                <div className="overflow-x-auto gts-scroll">
                  <table className="w-full text-sm">
                    <thead><tr className="text-muted-foreground text-xs border-b"><th className="text-left px-4 py-2">Guru/Staf</th><th className="text-right px-4 py-2">Total Sesi</th><th className="text-right px-4 py-2">Total Fee</th></tr></thead>
                    <tbody>
                      {yearly?.per_teacher.map((t, i) => (
                        <tr key={i} className="border-b last:border-0"><td className="px-4 py-2 font-medium text-gtsnavy">{t.teacher_name}</td><td className="px-4 py-2 text-right">{t.total_sessions}</td><td className="px-4 py-2 text-right font-semibold">{formatRupiah(t.total_fee)}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
              <Card className="rounded-2xl border-none shadow-[0_8px_30px_rgb(16,42,86,0.06)] bg-white overflow-hidden">
                <div className="px-5 py-3 bg-gtsnavy text-white font-nunito font-bold">Penerimaan per Sekolah · {yYear}</div>
                <div className="overflow-x-auto gts-scroll">
                  <table className="w-full text-sm">
                    <thead><tr className="text-muted-foreground text-xs border-b"><th className="text-left px-4 py-2">Sekolah</th><th className="text-right px-4 py-2">Total Sesi</th><th className="text-right px-4 py-2">Penerimaan</th></tr></thead>
                    <tbody>
                      {yearly?.per_school.map((s, i) => (
                        <tr key={i} className="border-b last:border-0"><td className="px-4 py-2 font-medium text-gtsnavy">{s.school_name}</td><td className="px-4 py-2 text-right">{s.total_sessions}</td><td className="px-4 py-2 text-right font-semibold">{formatRupiah(s.total_penerimaan)}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>

            <Card className="rounded-2xl border-none shadow-[0_8px_30px_rgb(16,42,86,0.06)] bg-white overflow-hidden">
              <div className="px-5 py-3 bg-gtsorange text-white font-nunito font-bold">Saldo Bersih per Bulan · {yYear}</div>
              <div className="overflow-x-auto gts-scroll">
                <table className="w-full text-sm">
                  <thead><tr className="text-muted-foreground text-xs border-b"><th className="text-left px-4 py-2">Bulan</th><th className="text-right px-4 py-2">Penerimaan</th><th className="text-right px-4 py-2">Pengeluaran</th><th className="text-right px-4 py-2">Saldo</th></tr></thead>
                  <tbody>
                    {yearly?.monthly.map((m) => (
                      <tr key={m.month} className="border-b last:border-0" data-testid={`yearly-month-${m.month}`}>
                        <td className="px-4 py-2 font-medium text-gtsnavy">{MONTHS[m.month - 1]}</td>
                        <td className="px-4 py-2 text-right">{formatRupiah(m.penerimaan)}</td>
                        <td className="px-4 py-2 text-right">{formatRupiah(m.pengeluaran)}</td>
                        <td className={`px-4 py-2 text-right font-semibold ${saldoColor(m.saldo)}`}>{formatRupiah(m.saldo)}</td>
                      </tr>
                    ))}
                    {yearly && (
                      <tr className="bg-gtscream font-nunito font-extrabold text-gtsnavy">
                        <td className="px-4 py-2.5">Total Setahun</td>
                        <td className="px-4 py-2.5 text-right">{formatRupiah(yearly.total_penerimaan)}</td>
                        <td className="px-4 py-2.5 text-right">{formatRupiah(yearly.total_pengeluaran)}</td>
                        <td className={`px-4 py-2.5 text-right ${saldoColor(yearly.total_saldo)}`} data-testid="yearly-total-saldo">{formatRupiah(yearly.total_saldo)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
